import { useMemo } from 'react'

import { usePenilikStatus } from './useSummary'
import {
  useAPIGetPraktikKinerjaFormValues,
  useAPIPostPraktikKinerjaFormValues,
} from 'api/kinerja/penilik-pegawai/pelaksanaan/form'
import { getPelaksanaanPraktikKinerjaPageState } from 'app/KinerjaPenilikPegawaiPage/utils/praktikKinerja'

import type { QueryStatus } from 'react-query'
import type {
  PenilaianPraktikKinerjaFormValues,
  PraktikKinerjaFormName,
  PenilikStatus,
  SubmissionResponse,
} from 'types/kinerja-penilik-pegawai'

export type PelaksanaanPraktikKinerjaProps<T, U> = {
  penilikSummaryHookResult: {
    summary: PenilikStatus
    summaryFetchStatus: QueryStatus
    isPelaksanaanState: boolean
  }

  skpId: string

  formData: PenilaianPraktikKinerjaFormValues
  formStatus: QueryStatus

  parsedFormValues: T
  parsedRating: U

  submitFormValues: ({
    values,
    saveAsDraft,
  }: {
    formName: PraktikKinerjaFormName
    values: T
    saveAsDraft: boolean
  }) => Promise<SubmissionResponse>
  submitStatus: QueryStatus

  isLoading: boolean
  isSuccess: boolean

  isFetchingFormValues: boolean
  isSuccessfullyFetchedFormValues: boolean
  isFailedToFetchFormValues: boolean
  dataFormValues: PenilaianPraktikKinerjaFormValues

  isPostingFormValues: boolean
  isSuccessfullyPostedFormValues: boolean
  isFailedToPostFormValues: boolean

  isViewOnly: boolean
  isLocked: boolean
  isNeedRollbackApproval: boolean
  isAppeal: boolean
}

export function usePelaksanaanPraktikKinerja<T = any, U = any>({
  formName,
  skpId: providedSkpId,
}: {
  formName: PraktikKinerjaFormName
  skpId?: string
}): PelaksanaanPraktikKinerjaProps<T, U> {
  const penilikSummaryHookResult = usePenilikStatus({
    disabled: !!providedSkpId,
  })

  const {
    summary: penilikSummary,
    summaryFetchStatus: penilikSummaryFetchStatus,
  } = penilikSummaryHookResult

  const skpId = providedSkpId ?? penilikSummary?.skpId ?? ''
  const enabledAPI = penilikSummaryFetchStatus === 'success' || !!skpId

  const {
    data: formData,
    status: formStatus,
    isFetching: isFetchingFormValues,
    isSuccess: isSuccessfullyFetchedFormValues,
    isError: isFailedToFetchFormValues,
  } = useAPIGetPraktikKinerjaFormValues({
    skpId,
    formName,
    opts: {
      enabled: enabledAPI,
    },
  })

  const parsedFormValues = useMemo<T | null>(() => {
    try {
      if (formStatus !== 'success') return null
      return JSON.parse(formData?.formValues ?? '{}') as T
    } catch (e) {
      console.error(e)
      return null
    }
  }, [formStatus, formData?.formValues])

  const parsedRating = useMemo<U | null>(() => {
    try {
      if (formStatus !== 'success') return null
      return JSON.parse(formData?.rating ?? '{}') as U
    } catch (e) {
      console.error(e)
      return null
    }
  }, [formStatus, formData?.rating])

  const {
    mutateAsync,
    status: submitStatus,
    isLoading: isPostingFormValues,
    isSuccess: isSuccessfullyPostedFormValues,
    isError: isFailedToPostFormValues,
  } = useAPIPostPraktikKinerjaFormValues({
    skpId,
    formName,
  })

  const { isViewOnly, isLocked, isNeedRollbackApproval, isAppeal } =
    getPelaksanaanPraktikKinerjaPageState(
      penilikSummary?.pelaksanaanStatuses?.praktikKinerja,
      formName,
      penilikSummary?.pelaksanaanStatuses?.praktikKinerjaPerubahanStatus
    )

  const submitFormValues = ({
    formName,
    values,
    saveAsDraft,
  }: {
    formName: PraktikKinerjaFormName
    values: T
    saveAsDraft: boolean
  }) =>
    mutateAsync({
      formValues: JSON.stringify(values),
      saveAsDraft,
      formName,
    })

  const isLoading =
    (!providedSkpId && penilikSummaryFetchStatus === 'loading') ||
    formStatus === 'loading'

  const isSuccess =
    (!!providedSkpId || penilikSummaryFetchStatus === 'success') &&
    formStatus === 'success'

  return {
    penilikSummaryHookResult,

    skpId,

    formData,
    formStatus,

    parsedFormValues,
    parsedRating,

    submitFormValues,
    submitStatus,

    isLoading,
    isSuccess,

    isFetchingFormValues,
    isSuccessfullyFetchedFormValues,
    isFailedToFetchFormValues,
    dataFormValues: { ...formData }, // alias to formData

    isPostingFormValues,
    isSuccessfullyPostedFormValues,
    isFailedToPostFormValues,

    // Page state
    isViewOnly,
    isLocked,
    isNeedRollbackApproval,
    isAppeal,
  }
}
